import { useEffect, useRef, useState } from 'react'

import { setStateAndDispatch } from '../utils/react'

import { useConstant } from './useConstant'

/**
 * @typedef {(value: SetStateAction<TValue>, timeout: number) => Promise<TValue>} DebouncedDispatch
 * @template {*} TValue
 */

/**
 * @param {React.RefObject<number | undefined>} timeoutRef
 */
function clearTimeoutRef(timeoutRef) {
  clearTimeout(timeoutRef.current)
  timeoutRef.current = void 0
}

/**
 * The {@link useDebouncedState} returns a debounced stateful value,
 * and a function to update it.
 *
 * Debounced state is updated once after the “cooldown” period.
 * Good for processing the final result.
 *
 * @usage
 * `const [state, setDebouncedState] = useDebouncedState()`
 *
 * The `setDebouncedState` function accepts two arguments:
 *  `nextState` - next state or function to get the next state from
 *      the current state.
 *  `timeout` - timeout in ms
 *
 * The `setDebouncedState` returns a promise which resolves with updated
 *  state value.
 *
 * @note
 * The `setDebouncedState` function will not change during component life-cycle,
 * so it can be safely added to effects / callbacks dependencies.
 *
 * @example
 * ```
 *  const [value, setValue] = useDebouncedState(0)
 *
 *  const handleChange = useCallback((nextValue) => {
 *    setValue(nextValue, 500)
 *  }, [setValue])
 * ```
 *
 * @template S
 * @param {S | (() => S)} [initialState]
 *
 * @returns {[S, DebouncedDispatch<S>]}
 */
export function useDebouncedState(initialState) {
  const [value, setValue] = useState(initialState)

  const timeoutHandleRef = useRef(null)

  const setDebouncedValue = useConstant(
    () =>
      (nextValue, timeout = 0) =>
        new Promise(resolve => {
          clearTimeoutRef(timeoutHandleRef)

          timeoutHandleRef.current = setTimeout(() => {
            if (timeoutHandleRef.current != null) {
              setStateAndDispatch(setValue, nextValue, resolve)
            }
          }, timeout)
        })
  )

  useEffect(
    () => () => {
      clearTimeout(timeoutHandleRef.current)
      timeoutHandleRef.current = null
    },
    []
  )

  return [value, setDebouncedValue]
}
